import { isPaymentAccount } from '@outss/ledger-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Download,
  Lock,
  Printer,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { AmountInput } from '@/components/amount-input';
import { Combobox } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { VatStatusBadge } from '@/components/vat';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate, todayIso } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import { useOrgId } from '@/lib/org';
import {
  ADJUSTABLE_BOXES,
  boxSection,
  downloadCsv,
  negate,
  useVatExceptions,
  useVatReturn,
  vatReturnCsv,
  type VatBox,
  type VatReturn,
} from '@/lib/vat';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/vat/returns/$key')({ component: VatReturnPage });

const SECTIONS: Array<{ key: ReturnType<typeof boxSection>; title: string }> = [
  { key: 'sales', title: 'VAT on sales and all other outputs' },
  { key: 'expenses', title: 'VAT on expenses and all other inputs' },
  { key: 'net', title: 'Net VAT due' },
];
const TOTALS = new Set(['8', '11', '12', '13', '14']);

/** Screen T2 (document 06): the VAT 201 working for one period. */
function VatReturnPage() {
  const { key } = Route.useParams();
  const orgId = useOrgId();
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const ret = useVatReturn(key);
  const exceptions = useVatExceptions(key);
  const [dialog, setDialog] = useState<null | 'file' | 'pay' | 'reopen' | 'adjust'>(null);
  const r = ret.data;

  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  if (ret.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (ret.isError || !r)
    return <EmptyState title="Could not load the VAT return" hint={ret.error?.message} />;

  const filed = r.filing !== null;
  const refund = r.netVat.startsWith('-');
  const outstanding = outstandingOf(r);
  const canPay = staff && filed && outstanding !== '0.00';
  const canAdjust = staff && !filed;
  const derivedAdjusted = r.adjustments.length > 0;

  return (
    <>
      <Link
        to="/taxes/vat"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline print:hidden"
      >
        <ArrowLeft className="size-4" /> Back to VAT
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-display text-[1.7rem] font-medium tracking-[-0.01em]">
            VAT return {r.label} <VatStatusBadge r={r} />
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {r.orgName} · TRN {formatTrn(r.trn) || '—'} · {displayDate(r.periodStart)} to{' '}
            {displayDate(r.periodEnd)} · Due {displayDate(r.dueDate)}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Export to Excel (CSV)"
            title="Export to Excel (CSV)"
            onClick={() => downloadCsv(`VAT return ${r.label}.csv`, vatReturnCsv(r))}
          >
            <Download className="size-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Print" onClick={() => window.print()}>
            <Printer className="size-4" />
          </Button>
          {canAdjust ? (
            <Button variant="outline" onClick={() => setDialog('adjust')}>
              Add adjustment
            </Button>
          ) : null}
          {r.canFile ? <Button onClick={() => setDialog('file')}>Mark as filed</Button> : null}
          {canPay ? (
            <Button onClick={() => setDialog('pay')}>
              {refund ? 'Record refund' : 'Record payment'}
            </Button>
          ) : null}
          {r.canReopen ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" aria-label="More actions">
                  More <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setDialog('reopen')}>
                  Reopen return
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <Stat
          label={refund ? 'Net VAT refundable' : 'Net VAT payable'}
          value={`${r.currency} ${formatAmount(refund ? negate(r.netVat) : r.netVat)}`}
          strong
        />
        <Stat label="VAT on sales (box 12)" value={formatAmount(r.outputVat)} />
        <Stat label="VAT on purchases (box 13)" value={formatAmount(r.inputVat)} />
        <Stat
          label={refund ? 'Refunded' : 'Paid'}
          value={filed ? formatAmount(r.settled) : '—'}
          hint={
            filed && outstanding !== '0.00' ? `${formatAmount(outstanding)} outstanding` : undefined
          }
        />
      </div>

      {r.filing ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
          <Lock className="size-4 text-muted-foreground" aria-hidden />
          <span>
            Filed on {r.filedOn ? displayDate(r.filedOn) : ''}
            {r.filing.filedBy ? ` by ${r.filing.filedBy}` : ''}
            {r.filing.reference ? ` · FTA reference ${r.filing.reference}` : ''}
          </span>
          {r.filing.returnEntryId ? (
            <span>
              · VAT return journal{' '}
              <Link
                to="/accounting/journal-entries/$entryId"
                params={{ entryId: r.filing.returnEntryId }}
                className="text-primary hover:underline"
              >
                no. {r.filing.returnEntryNo}
              </Link>
            </span>
          ) : null}
          <span className="text-muted-foreground">
            · VAT in this period is locked; date corrections in an open period.
          </span>
        </div>
      ) : r.status === 'open' ? (
        <div className="mb-4 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
          The period is still running: these are the totals so far. The return can be filed after{' '}
          {displayDate(r.periodEnd)}.
        </div>
      ) : null}
      {r.filing?.changedSinceFiling ? (
        <div className="mb-4 rounded-md border border-[#B45309]/30 bg-[#B45309]/5 px-4 py-3 text-sm text-[#B45309]">
          The books now give different figures for this period than the filed return. The filed
          figures are shown; see the exceptions below.
        </div>
      ) : null}
      {derivedAdjusted && !filed ? (
        <div className="mb-4 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
          Adjustments change the return only. Net VAT in the books for this period is {r.currency}{' '}
          {formatAmount(r.bookedNetVat)}; post a journal entry for the effect of the adjustments on
          the VAT accounts.
        </div>
      ) : null}

      <div className="mb-6 rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-[56px] px-4 py-2 text-left font-medium">Box</th>
                <th className="py-2 text-left font-medium">Description</th>
                <th className="w-[160px] py-2 text-right font-medium">Amount ({r.currency})</th>
                <th className="w-[160px] py-2 text-right font-medium">VAT ({r.currency})</th>
                <th className="w-[150px] px-4 py-2 text-right font-medium">Adjustment</th>
              </tr>
            </thead>
            {SECTIONS.map((sec) => (
              <tbody key={sec.key}>
                <tr className="bg-muted/50">
                  <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold">
                    {sec.title}
                  </td>
                </tr>
                {r.boxes
                  .filter((b) => boxSection(b.box) === sec.key)
                  .map((b) => (
                    <BoxRow key={b.box} r={r} b={b} />
                  ))}
              </tbody>
            ))}
          </table>
        </div>
      </div>

      <Section title={`Adjustments (${r.adjustments.length})`}>
        {r.adjustments.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            No adjustments. Use them for tourist refunds (box 2), import adjustments (box 7), bad
            debt relief and other corrections the books do not carry; each needs a reason.
          </p>
        ) : (
          <AdjustmentTable r={r} canRemove={canAdjust} />
        )}
      </Section>

      <Section
        title={`Exceptions${exceptions.data ? ` (${exceptions.data.length})` : ''}`}
        hint="Checks on this period's transactions. They do not change the boxes."
      >
        {exceptions.isPending ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">Checking…</p>
        ) : exceptions.isError ? (
          <p className="px-4 py-3 text-sm text-destructive">{exceptions.error.message}</p>
        ) : exceptions.data.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">No exceptions found.</p>
        ) : (
          <ul className="divide-y">
            {exceptions.data.map((x, i) => (
              <li key={i} className="flex gap-3 px-4 py-2.5 text-sm" data-kind={x.kind}>
                {x.severity === 'error' ? (
                  <AlertCircle
                    className="mt-0.5 size-4 shrink-0 text-[#B42318]"
                    aria-label="Error"
                  />
                ) : (
                  <AlertTriangle
                    className="mt-0.5 size-4 shrink-0 text-[#B45309]"
                    aria-label="Warning"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{x.title}</div>
                  <div className="text-muted-foreground">{x.message}</div>
                  {x.entryId ? (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {x.date ? displayDate(x.date) : ''} · {sourceLabel(x.sourceType ?? '')}{' '}
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: x.entryId }}
                        className="text-primary hover:underline"
                      >
                        no. {x.entryNo}
                      </Link>
                      {x.contactName ? ` · ${x.contactName}` : ''}
                      {x.memo ? ` · ${x.memo}` : ''}
                    </div>
                  ) : null}
                </div>
                {x.amount ? (
                  <div className="tnum shrink-0 text-right">{formatAmount(x.amount)}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {filed ? (
        <Section title={refund ? 'Refunds' : 'Payments'}>
          {r.payments.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {outstanding === '0.00' ? 'Nothing to pay.' : 'Nothing recorded yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-left font-medium">Date</th>
                    <th className="py-2 text-left font-medium">Type</th>
                    <th className="py-2 text-left font-medium">Account</th>
                    <th className="py-2 text-left font-medium">Reference</th>
                    <th className="py-2 text-left font-medium">Journal</th>
                    <th className="px-4 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {r.payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="px-4 py-2">{displayDate(p.date)}</td>
                      <td className="py-2">
                        {p.kind === 'payment' ? 'Payment to FTA' : 'Refund from FTA'}
                      </td>
                      <td className="py-2">{p.bankAccountName}</td>
                      <td className="py-2">{p.reference ?? ''}</td>
                      <td className="py-2">
                        <Link
                          to="/accounting/journal-entries/$entryId"
                          params={{ entryId: p.entryId }}
                          className="text-primary hover:underline"
                        >
                          no. {p.entryNo}
                        </Link>
                      </td>
                      <td className="tnum px-4 py-2 text-right">{formatAmount(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      ) : null}

      <FileDialog
        open={dialog === 'file'}
        r={r}
        exceptionCount={exceptions.data?.length ?? 0}
        onClose={() => setDialog(null)}
      />
      <PaymentDialog
        open={dialog === 'pay'}
        r={r}
        outstanding={outstanding}
        onClose={() => setDialog(null)}
      />
      <ReopenDialog open={dialog === 'reopen'} r={r} onClose={() => setDialog(null)} />
      <AdjustmentDialog open={dialog === 'adjust'} r={r} onClose={() => setDialog(null)} />
    </>
  );
}

function outstandingOf(r: VatReturn): string {
  if (!r.filing) return '0.00';
  const cents = (s: string) => {
    const neg = s.startsWith('-');
    const [w, f = ''] = (neg ? s.slice(1) : s).split('.');
    const v = BigInt(w!) * 100n + BigInt(f.padEnd(2, '0').slice(0, 2));
    return neg ? -v : v;
  };
  const net = cents(r.netVat);
  const due = (net < 0n ? -net : net) - cents(r.settled);
  const v = due < 0n ? 0n : due;
  return `${v / 100n}.${String(v % 100n).padStart(2, '0')}`;
}

function Stat({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  strong?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('tnum mt-1 text-right text-lg', strong && 'font-semibold')}>{value}</div>
      {hint ? <div className="text-right text-xs text-[#B45309]">{hint}</div> : null}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border bg-card">
      <header className="border-b px-4 py-2.5">
        <h2 className="text-base font-semibold">{title}</h2>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </header>
      {children}
    </section>
  );
}

function BoxRow({ r, b }: { r: VatReturn; b: VatBox }) {
  const total = TOTALS.has(b.box);
  const drill = (value: string | null) =>
    value === null ? null : (
      <Link
        to="/reports/vat-detail"
        search={{ from: r.periodStart, to: r.periodEnd, box: b.box }}
        className="text-primary hover:underline"
      >
        {formatAmount(value)}
      </Link>
    );
  const adj = [
    b.adjAmount && b.adjAmount !== '0.00' ? `Amount ${formatAmount(b.adjAmount)}` : null,
    b.adjVat && b.adjVat !== '0.00' ? `VAT ${formatAmount(b.adjVat)}` : null,
  ].filter(Boolean);
  return (
    <tr
      className={cn('border-b border-dashed last:border-0', total && 'font-semibold')}
      data-box={b.box}
    >
      <td className="px-4 py-1.5">{b.box}</td>
      <td className="py-1.5">{b.label}</td>
      <td className="tnum py-1.5 text-right">{drill(b.totalAmount)}</td>
      <td className="tnum py-1.5 text-right">{drill(b.totalVat)}</td>
      <td className="tnum px-4 py-1.5 text-right text-xs text-muted-foreground">
        {adj.join(' · ')}
      </td>
    </tr>
  );
}

function AdjustmentTable({ r, canRemove }: { r: VatReturn; canRemove: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/adjustments/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vat', orgId] });
      toast('Adjustment removed.');
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-[56px] px-4 py-2 text-left font-medium">Box</th>
            <th className="py-2 text-left font-medium">Reason</th>
            <th className="w-[160px] py-2 text-left font-medium">By</th>
            <th className="w-[130px] py-2 text-right font-medium">Amount</th>
            <th className="w-[130px] py-2 text-right font-medium">VAT</th>
            <th className="w-[56px] px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {r.adjustments.map((a) => (
            <tr key={a.id} className="border-b last:border-0">
              <td className="px-4 py-2">{a.box}</td>
              <td className="py-2">{a.reason}</td>
              <td className="py-2 text-muted-foreground">
                {a.createdBy ?? ''} · {displayDate(a.createdAt.slice(0, 10))}
              </td>
              <td className="tnum py-2 text-right">{formatAmount(a.amount)}</td>
              <td className="tnum py-2 text-right">{formatAmount(a.vat)}</td>
              <td className="px-4 py-2 text-right">
                {canRemove ? (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove adjustment to box ${a.box}`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useInvalidate() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['vat', orgId] });
}

function FileDialog({
  open,
  r,
  exceptionCount,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  exceptionCount: number;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  const [filedOn, setFiledOn] = useState(todayIso());
  const [reference, setReference] = useState('');
  const [postJournal, setPostJournal] = useState(true);
  useEffect(() => {
    if (open) {
      setFiledOn(todayIso());
      setReference('');
      setPostJournal(true);
    }
  }, [open]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/file`, {
        method: 'POST',
        body: JSON.stringify({ filedOn, reference: reference.trim(), postJournal }),
      }),
    onSuccess: async (saved) => {
      await invalidate();
      await qc.invalidateQueries({ queryKey: ['journal-entries'] });
      toast(`VAT return ${saved.label} marked as filed.`, {
        ...(saved.filing?.returnEntryId
          ? {
              link: {
                to: `/accounting/journal-entries/${saved.filing.returnEntryId}`,
                label: 'View journal',
              },
            }
          : {}),
      });
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark VAT return {r.label} as filed?</DialogTitle>
          <DialogDescription>
            The box figures are stored as filed and VAT in {displayDate(r.periodStart)} to{' '}
            {displayDate(r.periodEnd)} is locked: transactions with VAT can no longer be dated in
            this period. Net VAT {r.netVat.startsWith('-') ? 'refundable' : 'payable'}: {r.currency}{' '}
            {formatAmount(r.netVat.startsWith('-') ? negate(r.netVat) : r.netVat)}.
          </DialogDescription>
        </DialogHeader>
        {exceptionCount > 0 ? (
          <div className="rounded-md border border-[#B45309]/30 bg-[#B45309]/5 px-3 py-2 text-sm text-[#B45309]">
            {exceptionCount} exception{exceptionCount === 1 ? ' is' : 's are'} still listed for this
            period.
          </div>
        ) : null}
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3">
          <Field label="Date filed" error={errors['filedOn']}>
            {(id) => <DateInput id={id} value={filedOn} onChange={setFiledOn} />}
          </Field>
          <Field
            label="FTA reference (optional)"
            hint="The acknowledgement number from the FTA portal."
          >
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={60}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
          <Checkbox
            label="Post the VAT return journal (moves output and input VAT to VAT control)"
            checked={postJournal}
            onChange={(e) => setPostJournal(e.target.checked)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            {m.isPending ? 'Filing…' : 'Mark as filed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({
  open,
  r,
  outstanding,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  outstanding: string;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const accounts = useAccounts();
  const refund = r.netVat.startsWith('-');
  const banks = (accounts.data ?? []).filter(
    (a) => a.isActive && !a.isControl && isPaymentAccount(a.detailType),
  );
  const [date, setDate] = useState(todayIso());
  const [bank, setBank] = useState<string | null>(null);
  const [amount, setAmount] = useState(outstanding);
  const [reference, setReference] = useState('');
  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setAmount(outstanding);
      setReference('');
    }
  }, [open, outstanding]);
  useEffect(() => {
    if (!bank && banks[0]) setBank(banks[0].id);
  }, [bank, banks]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/payments`, {
        method: 'POST',
        body: JSON.stringify({ date, bankAccountId: bank, amount, reference: reference.trim() }),
      }),
    onSuccess: async (saved) => {
      await invalidate();
      const p = saved.payments.at(-1);
      toast(refund ? 'VAT refund recorded.' : 'VAT payment recorded.', {
        ...(p
          ? { link: { to: `/accounting/journal-entries/${p.entryId}`, label: 'View journal' } }
          : {}),
      });
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{refund ? 'Record VAT refund' : 'Record VAT payment'}</DialogTitle>
          <DialogDescription>
            {refund
              ? 'Posts the refund from the FTA: bank account debited, VAT control credited.'
              : 'Posts the payment to the FTA: VAT control debited, bank account credited.'}{' '}
            Outstanding on this return: {r.currency} {formatAmount(outstanding)}.
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3">
          <Field label="Date" error={errors['date']}>
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
          <Field label="Bank account" error={errors['bankAccountId']}>
            {() => (
              <Combobox
                ariaLabel="Bank account"
                items={banks.map((a) => ({ id: a.id, label: a.fullName, keywords: a.code }))}
                value={bank}
                onChange={setBank}
              />
            )}
          </Field>
          <Field label={`Amount (${r.currency})`} error={errors['amount']}>
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                value={amount}
                onValueChange={setAmount}
              />
            )}
          </Field>
          <Field
            label="Reference (optional)"
            hint="For example the e-Dirham or bank transfer number."
          >
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={60}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || !bank || !amount}>
            {m.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenDialog({ open, r, onClose }: { open: boolean; r: VatReturn; onClose: () => void }) {
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast(`VAT return ${r.label} reopened.`);
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen VAT return {r.label}?</DialogTitle>
          <DialogDescription>
            The filed figures are discarded, the VAT return journal is reversed today and VAT in the
            period is unlocked. Use this only when the return has not been submitted to the FTA, or
            before a correction is refiled. The reason is kept in the audit log.
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error?.message}</ErrorBanner>
        <Field label="Reason">
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => m.mutate()}
            disabled={m.isPending || reason.trim().length < 3}
          >
            Reopen return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentDialog({
  open,
  r,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const [box, setBox] = useState('2');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('decrease');
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setBox('2');
      setDirection('decrease');
      setAmount('');
      setVat('');
      setReason('');
    }
  }, [open]);
  const amountOnly = box === '4' || box === '5';
  const signed = (v: string) => (v ? (direction === 'decrease' ? `-${v}` : v) : '0');
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/adjustments`, {
        method: 'POST',
        body: JSON.stringify({
          box,
          amount: signed(amount),
          vat: amountOnly ? '0' : signed(vat),
          reason: reason.trim(),
        }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast(`Adjustment to box ${box} added.`);
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  const label = (b: string) => r.boxes.find((x) => x.box === b)?.label ?? '';
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add adjustment</DialogTitle>
          <DialogDescription>
            Changes a box on this return without posting to the books. Every adjustment needs a
            reason and is kept in the audit log.
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Box" className="sm:col-span-2" error={errors['box']}>
            {(id) => (
              <NativeSelect id={id} value={box} onChange={(e) => setBox(e.target.value)}>
                {ADJUSTABLE_BOXES.map((b) => (
                  <option key={b} value={b}>
                    {b} · {label(b)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Direction" className="sm:col-span-2">
            {(id) => (
              <NativeSelect
                id={id}
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'increase' | 'decrease')}
              >
                <option value="decrease">Decrease the box</option>
                <option value="increase">Increase the box</option>
              </NativeSelect>
            )}
          </Field>
          <Field label={`Amount (${r.currency})`} error={errors['amount']}>
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                value={amount}
                onValueChange={setAmount}
              />
            )}
          </Field>
          <Field label={`VAT (${r.currency})`} error={errors['vat']}>
            {(id) => (
              <AmountInput
                id={id}
                disabled={amountOnly}
                className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                value={amountOnly ? '' : vat}
                onValueChange={setVat}
              />
            )}
          </Field>
          <Field label="Reason" className="sm:col-span-2" error={errors['reason']}>
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate()}
            disabled={m.isPending || reason.trim().length < 3 || (!amount && !(vat && !amountOnly))}
          >
            Add adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
